import { Box, Drawer, Typography, IconButton, Stack } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useForm, useFieldArray } from 'react-hook-form';
import Button from 'components/Common/Button';
import Select from 'components/Common/Select';
import { IoClose } from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetMenuOptions,
  useGetMenuItems,
  useGetMenuItemModifiers,
  useGetAllItems,
  useGetUpSellAndCrossSell,
  useCreateUpSellAndCrossSell,
  useUpdateUpSellAndCrossSell
} from '@/apis/business/setting/integrations/StreamOrderSettings/hook';
import { useGetIntegration } from '@/apis/business/setting/integrations/hook';
import UpsellCrossSellFormSkeleton from './UpsellCrossSellFormSkeleton';
import { RiDeleteBin6Line } from 'react-icons/ri';

export default function UpsellCrossSellForm() {
  const navigate = useNavigate();
  const { upSellAndCrossSellId, integrationId } = useParams();
  const { data: IntegrationData } = useGetIntegration(Number(integrationId));
  const locationId = IntegrationData?.locations?.[0]?.id;
  const { data: menus } = useGetMenuOptions(locationId);
  const { data: allItems, isLoading: loadingAllItems } = useGetAllItems(locationId);

  const { data: UpSellAndCrossSellData, isLoading } = useGetUpSellAndCrossSell(
    Number(integrationId),
    Number(upSellAndCrossSellId)
  );

  const createMutation = useCreateUpSellAndCrossSell(Number(integrationId), Number(upSellAndCrossSellId));
  const updateMutation = useUpdateUpSellAndCrossSell(Number(integrationId), Number(upSellAndCrossSellId));

  const { control, handleSubmit, watch, setValue } = useForm<any>({
    defaultValues: {
      selected_menu_item_ids: [],
      pos_modifier_ids: [{ id: '' }],
      pos_menu_item_ids: [{ item_id: '' }]
    },
    values: {
      pos_menu_id: UpSellAndCrossSellData?.pos_menu?.id,
      selected_menu_item_ids: UpSellAndCrossSellData?.selected_menu_item_ids ?? [],
      pos_modifier_ids: UpSellAndCrossSellData?.pos_modifiers.length
        ? UpSellAndCrossSellData?.pos_modifiers?.map((item: any) => ({
            id: item.id
          }))
        : [{ id: '' }],
      pos_menu_item_ids: UpSellAndCrossSellData?.pos_menu_items.length
        ? UpSellAndCrossSellData?.pos_menu_items?.map((item: any) => ({
            item_id: item.id
          }))
        : [{ item_id: '' }]
    }
  });

  const selectedMenuId = Number(watch('pos_menu_id'));
  const isModifiersDisabled = watch('selected_menu_item_ids')?.length > 1;
  const itemId = watch('selected_menu_item_ids')?.[0];

  const { data: menuItems, isLoading: loadingMenuItems } = useGetMenuItems(locationId, selectedMenuId, {
    source: 'upsell_crosssell',
    record_id: upSellAndCrossSellId
  });
  const { data: modifiers, isLoading: modifiersLoading } = useGetMenuItemModifiers(locationId, selectedMenuId, itemId);

  const {
    fields: upsellFields,
    append: appendUpsell,
    remove: removeUpSell
  } = useFieldArray({
    control,
    name: 'pos_modifier_ids'
  });

  const {
    fields: crossSellFields,
    append: appendCrossSell,
    remove
  } = useFieldArray({
    control,
    name: 'pos_menu_item_ids'
  });

  const onSubmit = (data: any) => {
    const payload = {
      ...data,
      pos_menu_item_ids: data.pos_menu_item_ids?.map((item: any) => item.item_id),
      pos_modifier_ids: data.pos_modifier_ids?.map((item: any) => item.id)
    };

    if (upSellAndCrossSellId) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  if (upSellAndCrossSellId && isLoading) {
    return <UpsellCrossSellFormSkeleton />;
  }

  return (
    <Drawer
      anchor='right'
      open
      onClose={() => navigate(-1)}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400, md: 720 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 100001
        }
      }}
    >
      {/* Header */}
      <Box px={3} py={2} borderBottom='1px solid #DEDEDE' display='flex' justifyContent='space-between'>
        <Typography variant='h6'>{upSellAndCrossSellId ? 'Update' : 'Add'} Upsell & Cross-sell</Typography>
        <IconButton onClick={() => navigate(-1)} size='small'>
          <IoClose size={24} />
        </IconButton>
      </Box>

      <Box
        onSubmit={handleSubmit(onSubmit)}
        component='form'
        sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
      >
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          <Grid container spacing={2}>
            <Grid size={6}>
              <Select
                name='pos_menu_id'
                control={control}
                label='Select Menu'
                fullWidth
                options={menus?.data?.map((m) => ({ label: m.name, value: m.id })) || []}
                onChange={(e) => {
                  setValue('pos_menu_id', e?.target.value);
                  setValue('selected_menu_item_ids', []);
                  setValue('pos_modifier_ids', [{ id: '' }]);
                  setValue('pos_menu_item_ids', [{ item_id: '' }]);
                }}
                required
              />
            </Grid>

            <Grid size={6}>
              <Select
                name='selected_menu_item_ids'
                control={control}
                label='Select Item'
                disabled={loadingMenuItems}
                fullWidth
                multiple
                options={menuItems?.data?.map((i) => ({ label: i.name, value: i.id })) || []}
                onChange={(e) => {
                  setValue('selected_menu_item_ids', e?.target.value);
                  setValue('pos_modifier_ids', [{ id: '' }]);
                  setValue('pos_menu_item_ids', [{ item_id: '' }]);
                }}
                required
              />
            </Grid>
          </Grid>

          {selectedMenuId && itemId ? (
            <Grid container spacing={2} mt={2}>
              <Grid size={6}>
                <Box border='1px solid #ECECEC' borderRadius='6px' p={2}>
                  <Typography variant='subtitle2' mb={2}>
                    Upsell (Modifiers)
                  </Typography>
                  {upsellFields.map((field, index) => (
                    <Stack key={field.id} direction='row'>
                      <Grid size={10}>
                        <Select
                          key={field.id}
                          name={`pos_modifier_ids.${index}.id`}
                          disabled={modifiersLoading || isModifiersDisabled}
                          control={control}
                          label='Upsell (Modifiers)'
                          fullWidth
                          sx={{ mb: 1.5 }}
                          options={
                            modifiers?.data?.map((m) => ({
                              label: m.name,
                              value: m.id
                            })) || []
                          }
                        />
                      </Grid>
                      <Grid size={2}>
                        {upsellFields.length > 1 && (
                          <IconButton onClick={() => removeUpSell(index)} color='error'>
                            <RiDeleteBin6Line />
                          </IconButton>
                        )}
                      </Grid>
                    </Stack>
                  ))}
                  {!isModifiersDisabled && (
                    <Box display='flex' justifyContent='center'>
                      <Button variant='outlined' onClick={() => appendUpsell({ id: '' })}>
                        + Add More
                      </Button>
                    </Box>
                  )}
                </Box>
              </Grid>

              <Grid size={6}>
                <Box border='1px solid #ECECEC' borderRadius='6px' p={2}>
                  <Typography variant='subtitle2' mb={2}>
                    Cross Sell Items
                  </Typography>
                  {crossSellFields.map((field, index) => (
                    <Stack key={field.id} direction='row'>
                      <Grid size={10}>
                        <Select
                          key={field.id}
                          name={`pos_menu_item_ids.${index}.item_id`}
                          control={control}
                          label='Cross Sell Items'
                          disabled={loadingAllItems}
                          fullWidth
                          sx={{ mb: 1.5 }}
                          options={
                            allItems?.data
                              ?.filter((i) => i.id !== itemId)
                              ?.map((i) => ({
                                label: i.name,
                                value: i.id
                              })) || []
                          }
                        />
                      </Grid>
                      <Grid size={2}>
                        {crossSellFields.length > 1 && (
                          <IconButton onClick={() => remove(index)} color='error'>
                            <RiDeleteBin6Line />
                          </IconButton>
                        )}
                      </Grid>
                    </Stack>
                  ))}

                  <Box display='flex' justifyContent='center'>
                    <Button variant='outlined' onClick={() => appendCrossSell({ item_id: '' })}>
                      + Add More
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          ) : (
            ''
          )}
        </Box>

        {/* Footer */}
        <Box px={3} py={2} borderTop='1px solid #DEDEDE' display='flex' justifyContent='flex-end' gap={2}>
          <Button variant='outlined' color='secondary' onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button type='submit' variant='contained' isPending={createMutation.isPending || updateMutation.isPending}>
            {upSellAndCrossSellId ? 'Save' : 'Add'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
